import { cloneDeep } from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { Alert, Anchor, Button, Group, Tabs } from "@mantine/core";
import { IImport } from "../../types/imports/import";

import { getAllCompetitions } from "../../data/actions/competitionActions";
import { getImport, triggerImport } from "../../data/actions/importActions";
import { getAllTeams } from "../../data/actions/teamActions";
import { getAllVenues } from "../../data/actions/venueActions";

import { IState } from "../../data/reducers/state";
import { ImportStatus } from "../../types/imports/ImportStatus";
import { IImportGame } from "../../types/imports/importgame";
import { CenteredLoader } from "../common/loader";
import CompetitionMapTab from "./CompetitionMapTab";
import TeamMapTab from "./TeamMapTab";
import VenueMapTab from "./VenueMapTab";

interface SportingpulseImportFormProps {
  importId: string;
  imports: { [id: string]: IImport };
  actions: {
    getImport: Function;
    getAllVenues: Function;
    getAllTeams: Function;
    getAllCompetitions: Function;
    triggerImport: Function;
  };

  onCancel?: Function;
}

interface SportingpulseImportFormState {
  import: IImport | null;
  importId: string;
  importChanged: Partial<IImport>;
  hasChanges: boolean;
  tab: string;
  saving: boolean;
  error?: string | null;
}

const classnames = (props) =>
  Object.keys(props)
    .filter((k) => props[k])
    .join(" ");

function OverviewTab({ imp }) {
  const games: IImportGame[] = imp.data;
  const totalGames = games.length;
  const unmappedGames = games.filter((g) => !(g.isNew || g._id)).length;

  const unmappedVenues =
    imp.maps &&
    Object.keys(imp.maps.venues || {}).filter((v) => imp.maps.venues[v].isNew);
  let unmappedText: string | null = null;
  if (unmappedVenues.length > 0) {
    const s = unmappedVenues.length == 1 ? "" : "s";
    const vs = unmappedVenues.map((v) => imp.maps.venues[v].name).join(", ");
    unmappedText = `The venue${s} (${vs}) did not correctly map to a venue in the system,
        please ensure the venue names are clear or `;
  }

  return (
    <div>
      <h4>
        Status:
        {imp.status}
      </h4>
      <p>
        Importing:
        {totalGames} games
      </p>
      <p>
        This will create {unmappedGames} games, and may unsafely update{" "}
        {totalGames - unmappedGames}.
      </p>
      {unmappedText && (
        <Alert color="red">
          {unmappedText}{" "}
          <a href="https://support.stadiumsupervisor.com.au">raise an issue.</a>
        </Alert>
      )}
    </div>
  );
}

const SkippedTab = ({ strings }) =>
  strings.map((r, idx) => <p key={idx}>{r}</p>);

class SportingpulseImportForm extends React.PureComponent<
  SportingpulseImportFormProps,
  SportingpulseImportFormState
> {
  constructor(props: SportingpulseImportFormProps) {
    super(props);

    this.props.actions.getAllVenues();
    this.props.actions.getAllCompetitions();
    this.props.actions.getAllTeams();

    this.mapValueChange = this.mapValueChange.bind(this);
    this.importJob = this.importJob.bind(this);

    const imp = props.imports[props.importId];

    this.state = {
      tab: "Overview",
      importId: props.importId,
      import: cloneDeep(imp),
      importChanged: { _id: props.importId },
      hasChanges: false,
      saving: false,
    };
    if (!imp.maps) {
      props.actions.getImport(this.props.importId);
    }
  }

  componentWillReceiveProps(nextProps: Readonly<SportingpulseImportFormProps>) {
    // return if it's the same person
    this.trySetNewImport(nextProps.importId, nextProps);
  }

  mapValueChange(
    mapType: "venues" | "teams" | "competitions",
    key: string,
    newValue,
  ) {
    const imp = this.state.import;
    const impChanged = this.state.importChanged;
    if (!imp) return;

    this.setState((state) => ({
      ...state,
      import: {
        ...imp,
        maps: {
          ...imp.maps,
          [mapType]: {
            ...imp.maps[mapType],
            [key]: {
              ...imp.maps[mapType][key],
              ...newValue,
            },
          },
        },
      },
      importChanged: {
        ...impChanged,
        maps: {
          ...impChanged.maps,
          [mapType]: {
            ...imp.maps[mapType],
            [key]: {
              ...imp.maps[mapType][key],
              ...newValue,
            },
          },
        },
      },
    }));
  }

  trySetNewImport(importId: string, props: SportingpulseImportFormProps) {
    const importJobFromProps = this.props.imports[this.props.importId];

    if (!importJobFromProps.maps) {
      return props.actions.getImport(this.props.importId);
    }

    // we should always update if we don't have changes or we get a different importId or we don't have state yet
    const shouldUpdate =
      !!importJobFromProps.maps &&
      (!this.state ||
        !this.state.import ||
        !this.state.hasChanges ||
        importId !== this.state.importId);
    if (!shouldUpdate) return;

    console.log("We've received a new person");
    this.setState((state) => ({
      ...state,
      importId,
      import: cloneDeep(props.imports[importId]),
      importChanged: { _id: importId },
      hasChanges: false,
    }));
  }

  toggle(tabKey) {
    this.setState((state) => ({ ...state, tab: tabKey }));
  }

  importJob() {
    this.setState((state) => ({ ...state, saving: true, error: null }));
    this.props.actions
      .triggerImport(
        this.state.importChanged._id,
        this.state.importChanged.maps,
      )
      .then((imp) => {
        this.setState(
          (state) =>
            ({
              ...state,
              saving: false,
              hasChanges: false,
              import: { ...state.import, status: ImportStatus.imported },
            }) as SportingpulseImportFormState,
        );
      })
      .catch((er) => {
        this.setState((state) => ({
          ...state,
          saving: false,
          error: er.message,
        }));
      });
  }

  render() {
    if (!this.state) return <CenteredLoader />;

    const imp = this.state.import;

    if (!(imp && imp.maps)) return <CenteredLoader />;

    const tabs = ["Overview", "Venues", "Competitions", "Teams"];

    if (imp.skipped && imp.skipped.length > 0) {
      tabs.push("Skipped");
    }

    return (
      <div>
        <p>
          <em>This page is under construction</em>
        </p>
        <Tabs value={this.state.tab} onChange={(value) => this.toggle(value || "")}>
          <Tabs.List>
            {tabs.map((k) => (
              <Tabs.Tab key={k} value={k}>
                {k}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel value="Overview">
            <OverviewTab imp={imp} />
          </Tabs.Panel>
          <Tabs.Panel value="Venues">
            <VenueMapTab
              venues={imp.maps.venues}
              onChange={this.mapValueChange}
            />
          </Tabs.Panel>
          <Tabs.Panel value="Competitions">
            <CompetitionMapTab
              competitions={imp.maps.competitions}
              onChange={this.mapValueChange}
            />
          </Tabs.Panel>
          <Tabs.Panel value="Teams">
            <TeamMapTab teams={imp.maps.teams} onChange={this.mapValueChange} />
          </Tabs.Panel>
          {imp.skipped && imp.skipped.length > 0 && (
            <Tabs.Panel value="Skipped">
              <SkippedTab strings={imp.skipped.map((a) => JSON.stringify(a))} />
            </Tabs.Panel>
          )}
        </Tabs>
        <hr />
        {this.state.error && <Alert color="red">{this.state.error}</Alert>}
        {this.state.saving && <CenteredLoader />}

        <Button
          color="gray"
          style={{ float: "left" }}
          onClick={() => this.props.onCancel?.()}
        >
          Close
        </Button>
        {this.state.import &&
          this.state.import.status === ImportStatus.queued && (
            <Button
              color="blue"
              style={{ float: "right", width: "80px" }}
              onClick={this.importJob}
            >
              Import
            </Button>
          )}
      </div>
    );
  }
}

function mapStateToProps(state: IState, props: SportingpulseImportFormProps) {
  return {
    imports: state.imports.map,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        getImport,
        getAllVenues,
        getAllTeams,
        getAllCompetitions,
        triggerImport,
      },
      dispatch,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SportingpulseImportForm);
